/**
 * Gộp các lần gọi GET giống nhau trong thời gian ngắn: request đang chờ thì mọi nơi dùng chung
 * một promise, xong rồi thì dùng lại kết quả thêm `ttlMs`. Dùng khi nhiều component cùng lúc
 * lấy một dữ liệu (vd mở hội thoại: khung chat, khung thông tin khách, form... cùng gọi chi tiết khách).
 *
 * - `fresh: true`: bỏ qua kết quả đang có, gọi lại và thay luôn cho các nơi gọi sau.
 * - Sau khi sửa dữ liệu thì gọi `invalidate(key)` (hoặc `invalidate()` xoá hết).
 * - Kết quả không thành công (`isOk` trả false) hoặc lỗi thì không giữ lại.
 * - Kết quả là object dùng chung: nơi gọi chỉ đọc, không sửa trực tiếp.
 */
export function createRequestCache<T>(ttlMs: number, isOk: (value: T) => boolean = () => true) {
    const entries = new Map<string, { promise: Promise<T>; expiresAt: number }>();

    const prune = (now: number) => {
        if (entries.size < 100) return;
        entries.forEach((entry, key) => {
            if (entry.expiresAt <= now) entries.delete(key);
        });
    };

    return {
        get(key: string, run: () => Promise<T>, fresh = false): Promise<T> {
            const now = Date.now();
            const hit = entries.get(key);
            if (!fresh && hit && hit.expiresAt > now) return hit.promise;

            prune(now);
            const promise = run();
            // Đang chờ: luôn dùng chung, hết chờ mới tính thời hạn
            const entry = { promise, expiresAt: Infinity };
            entries.set(key, entry);
            promise.then(
                (value) => {
                    // Đã bị xoá / thay bởi lần gọi mới hơn thì thôi
                    if (entries.get(key) !== entry) return;
                    if (isOk(value)) entry.expiresAt = Date.now() + ttlMs;
                    else entries.delete(key);
                },
                () => {
                    if (entries.get(key) === entry) entries.delete(key);
                },
            );
            return promise;
        },

        invalidate(key?: string) {
            if (key === undefined) entries.clear();
            else entries.delete(key);
        },
    };
}
